/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useKPIContext } from '@/providers/KpiProvider';
import { useLanguage } from '@/providers/LanguageProvider';
import { convertToUTCDate } from '@/utils/convertToUTCDate';
import {
  extractSankeyData,
  findRootNode,
  getDefaultNodesOrExpand,
  getPlaceholderTextFromNodeKey,
  handlePathHighlightOnNodeSelection,
} from '@/utils/sankeyChartUtils';
import { SankeyData, SankeyDataNode } from 'types/sankey';

import { useAppDetails } from '@/hooks/useAppDetails';
import { useEvals } from '@/hooks/useEvals';
import { Separator } from '@/components/ui/separator';
import { EvalModal } from '@/components/eval/eval-modal';
import { KPIItemsApplications } from '@/components/kpi/kpi-items-application';
import { NotFoundMessage } from '@/components/not-found';
import { ObservationCards } from '@/components/observations';
import { SankeyChart } from '@/components/sanky';
import ChartLoadingState from '@/components/sanky/ChartLoadingState';
import { RectNode } from '@/components/sanky/sankey-rect';

import {
  getPresetLabelToNameMap,
  getPresetRange,
  usePresets,
} from '../prompts/date-range-picker/calendar-helper/presets';
import { NodeDetails } from './node-details';

const InsightsPage = ({ appName }: { appName: string }) => {
  // constants
  const padding = { left: 90, right: 130, top: 20, bottom: 20 };
  const colors = ['#6CF2A2', '#34BCAF', '#257C9D', '#F26C74', '#FFCB78'];

  const searchParams = useSearchParams();
  const { messages } = useLanguage();
  const sankeyConstants = messages?.Sankey;
  // states
  const {
    sankeyData,
    sankeyDataLoading,
    factsLoading,
    fetchSankeyData,
    sankeyDataRefreshTime,
    fetchFactsData,
    factsData,
    setNodeKey,
    viewBy,
    setViewBy,
    selectedQualityFilter,
    setSelectedQualityFilter,
    factName,
    setFactName,
    workflowNames,
    setWorkflowNames,
    selectedFactFilter,
    setSelectedFactFilter,
    selectedFactIds,
    setSelectedFactIds,
  } = useKPIContext();
  const presets = usePresets();
  const router = useRouter();
  const pathname = usePathname();
  const appDetails = useAppDetails();
  const appData = appDetails?.data;
  const { defaultFact, setDefaultFact } = appDetails || {};
  const [rootNodeKey, setRootNodeKey] = useState<string | null>(null);
  const [formattedSankeyData, setFormattedSankeyData] = useState<
    SankeyData | undefined
  >();
  const [fullSankeyData, setFullSankeyData] = useState<
    SankeyData | undefined
  >();
  const [selectedNode, setSelectedNode] = useState<RectNode | null>(null);
  const [rootNode, setRootNode] = useState<RectNode | null>(null);
  const [evaluateOpen, setEvaluateOpen] = useState(false);
  const [previousFactName, setPreviousFactName] = useState<string>('');

  // extract node actions for the current view
  const targetView = selectedNode?.views?.find((view) =>
    Array.isArray(view[viewBy])
  );
  const actions = targetView?.[viewBy] || [];
  const evaluateAction = actions.find((action) => action.action === 'Evaluate');

  const evaluationFilters = {
    breakdown: evaluateAction?.filters?.breakdown ?? null,
    evalFilter: evaluateAction?.filters?.eval_filter ?? null,
  };

  const hasInitializedFactName = useRef(false);

  // functions
  const handleFactNameChange = (value: string) => {
    setFactName(value);
    setViewBy('All');
    setSelectedQualityFilter(null);
  };
  const handleClickedNode = useCallback(
    (data: SankeyDataNode) => {
      if (data.key === rootNodeKey && data.parentKey === null) {
        setRootNode(data);
      }

      setSelectedNode(data);
      setNodeKey(data.key);
    },
    [rootNodeKey]
  );

  // set filters
  const handleFilterApply = (
    workflows: string | null,
    factFilter: string | null,
    factIds: string | null
  ) => {
    setWorkflowNames(workflows);
    setSelectedFactFilter(factFilter);
    setSelectedFactIds(factIds);

    const params = new URLSearchParams(searchParams.toString());
    if (workflows) {
      params.set('workflow_names', workflows);
    } else {
      params.delete('workflow_names');
    }
    if (factFilter) {
      params.set('filter_fact', factFilter);
    } else {
      params.delete('filter_fact');
    }
    if (factIds) {
      params.set('filter_fact_ids', factIds);
    } else {
      params.delete('filter_fact_ids');
    }
    router.replace(`${pathname}?${params.toString()}`);
  };

  const filterCount =
    Number(!!workflowNames) + Number(!!(selectedFactFilter || selectedFactIds));

  const getRange = () => {
    const daysParam = searchParams.get('days');
    const startParam = searchParams.get('start');
    const endParam = searchParams.get('end');

    let apiRange;

    if (daysParam === 'all') {
      apiRange = {
        from: null,
        to: null,
      };
    } else if (startParam && endParam) {
      apiRange = {
        from: convertDateToUTC(new Date(startParam)),
        to: convertDateToUTC(new Date(endParam)),
      };
    } else {
      // Use app's default time range
      const presetMap = getPresetLabelToNameMap(presets);
      const defaultPresetName =
        presetMap[appData?.default_time_range] || 'last7';

      const range = getPresetRange(defaultPresetName, presets);
      //fallback.
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
      const now = new Date();

      apiRange = {
        from: convertDateToUTC(range.from || sevenDaysAgo),
        to: convertDateToUTC(range.to || now),
      };
    }

    return apiRange;
  };

  function getSankeyData() {
    const apiRange = getRange();
    fetchSankeyData(
      appName,
      factName,
      selectedQualityFilter?.value,
      apiRange,
      workflowNames,
      selectedFactFilter,
      selectedFactIds
    );
  }

  const { evaluateActions } = useEvals();

  useEffect(() => {
    fetchFactsData(appName);
  }, [appName]);

  // fetch eval templates in discovery mode when fact, app, or date range changes
  useEffect(() => {
    if (appName) {
      const range = getRange();
      evaluateActions.fetchEvalsTemplate(
        factName || 'traces',
        appName,
        range?.from?.toISOString() || undefined,
        range?.to?.toISOString() || undefined
      );
    }
  }, [factName, appName, searchParams]);

  /**
   * Initialize factName same as defaultFact if availabe, otherwise use the first fact from factsData
   */
  useEffect(() => {
    if (hasInitializedFactName.current) return;
    if (!factsData || factsData.length === 0) return;

    const hasDefaultFactInFacts =
      !!defaultFact &&
      factsData.some(
        (fact: { key: string; label: string }) => fact.key === defaultFact
      );

    const initialFactName = hasDefaultFactInFacts
      ? defaultFact
      : factsData[0].key;

    setFactName(initialFactName);

    if (!defaultFact) {
      setDefaultFact?.(factsData[0].key);
    }

    hasInitializedFactName.current = true;
  }, [factsData, defaultFact]);

  useEffect(() => {
    if (factName === '') return;

    setFormattedSankeyData(undefined);
    setSelectedNode(null);
    getSankeyData();
  }, [
    appName,
    searchParams,
    factName,
    selectedQualityFilter?.value,
    workflowNames,
  ]);

  useEffect(() => {
    if (!selectedNode || !formattedSankeyData || !rootNodeKey) return;

    const updatedData = handlePathHighlightOnNodeSelection(
      selectedNode,
      formattedSankeyData,
      rootNodeKey
    );

    setFormattedSankeyData(updatedData);
  }, [selectedNode?.key !== null]); //never ever put any object in deps array, if needed, stringify it, and make it as precise as possible.

  // converts the raw data to the format needed by SankeyChart
  useEffect(() => {
    if (sankeyData) {
      const rootNodeKey = findRootNode(sankeyData);
      setRootNodeKey(rootNodeKey);

      if (rootNodeKey) {
        const data = extractSankeyData(sankeyData);

        // Reset to "All" only when fact changes
        if (factName !== previousFactName && previousFactName !== '') {
          setPreviousFactName(factName);
          if (viewBy !== 'All') setViewBy('All');
        }

        if (previousFactName === '') {
          setPreviousFactName(factName);
        }

        // Auto-select root on first fetch
        if (!selectedNode && rootNodeKey) {
          setNodeKey(rootNodeKey);
        }

        setFullSankeyData(data); // store full data
        const visibleData = getDefaultNodesOrExpand(
          rootNodeKey,
          data,
          viewBy,
          true
        );
        setFormattedSankeyData(visibleData);
      }
    }
  }, [sankeyData, viewBy]);

  return (
    <>
      <KPIItemsApplications
        appName={appName}
        isLoading={factsLoading}
        factName={factName}
        handleFactNameChange={handleFactNameChange}
        factsData={factsData}
        selectedQualityFilter={selectedQualityFilter}
        setSelectedQualityFilter={setSelectedQualityFilter}
        onFilterApply={handleFilterApply}
        currentWorkflows={workflowNames}
        currentFactFilter={selectedFactFilter}
        currentFactIds={selectedFactIds}
        activeRange={getRange()}
        filterCount={filterCount}
        defaultFact={defaultFact}
        setDefaultFact={setDefaultFact}
      />
      <div className="mt-3 flex max-h-[492px] min-h-[418px] flex-col rounded-lg border border-line-secondary bg-card-00 p-[29px] shadow-md">
        {sankeyDataLoading || factsLoading ? (
          <ChartLoadingState />
        ) : (
          <>
            <div className="flex flex-1">
              {fullSankeyData &&
              formattedSankeyData &&
              formattedSankeyData.links.length &&
              formattedSankeyData.nodes.length &&
              rootNodeKey &&
              formattedSankeyData.nodes.some((n) => (n.value ?? 0) > 0) ? ( // check if at least one node has non-zero value
                <>
                  <div className="flex w-4/5 flex-col items-center justify-center">
                    <div className="h-full w-full">
                      <SankeyChart
                        data={fullSankeyData}
                        svgPadding={padding}
                        nodePadding={55}
                        nodeWidth={16}
                        colorPalette={colors}
                        onNodeClick={handleClickedNode}
                        rootNodeKey={rootNodeKey}
                      />
                    </div>
                  </div>
                  <Separator
                    className="mx-9 h-auto bg-line-tertiary"
                    orientation="vertical"
                  />
                  <NodeDetails
                    selectedNode={selectedNode}
                    onClear={() => {
                      setSelectedNode(rootNode);
                    }}
                    appName={appName}
                    factName={factName}
                    rootNodeKey={rootNodeKey}
                    setEvaluateOpen={setEvaluateOpen}
                    workflowNames={workflowNames}
                    selectedFilterFact={selectedFactFilter}
                    selectedFilterFactIDs={selectedFactIds}
                  />
                </>
              ) : (
                <div className="flex w-full items-center justify-center">
                  <NotFoundMessage
                    title={sankeyConstants?.not_found_message.title}
                    message={sankeyConstants?.not_found_message.message}
                    outerDivClassName="my-0 flex-1"
                  />
                </div>
              )}
            </div>

            {!sankeyDataLoading && sankeyDataRefreshTime && (
              <div className="flex justify-center">
                <span className="text-body-sm text-form-input-disabled">
                  {sankeyDataRefreshTime}
                </span>
              </div>
            )}
          </>
        )}
      </div>
      <ObservationCards />
      {evaluateOpen && (
        <EvalModal
          inferTitle={factName as 'traces' | 'inferences' | 'conversations'}
          open={evaluateOpen}
          breakdownFilter={evaluationFilters.breakdown ?? selectedNode?.key}
          evalFilters={evaluationFilters.evalFilter}
          placeholder={getPlaceholderTextFromNodeKey(
            selectedNode,
            formattedSankeyData
          )}
          setOpen={setEvaluateOpen}
          appName={appName}
          dateRange={getRange()}
        />
      )}
    </>
  );
};
export { InsightsPage };

function convertDateToUTC(date: Date) {
  const utcString = convertToUTCDate(date);
  return utcString === null ? utcString : new Date(utcString);
}
